import { ReactText, useCallback } from 'react';
import BigNumber from 'bignumber.js';

import { useTranslation } from 'modules/i18n';

import { ZERO } from '../consts';
import translation from './useValidateNumber.translation';

export type UseValidateNumberResult = (value?: ReactText) => string | undefined;

interface Props {
  balance?: BigNumber;
  max?: BigNumber;
  min?: BigNumber;
  unit?: string;
  integerOnly?: boolean;
  required?: boolean;
}

export const useValidateNumber = ({
  balance,
  max = balance,
  min = ZERO,
  unit = '',
  integerOnly = false,
  required = false,
}: Props): UseValidateNumberResult => {
  const { t, keys } = useTranslation(translation);

  return useCallback(
    (value?: ReactText) => {
      if (!value) {
        if (required) {
          return t(keys.required);
        }

        return;
      }

      const currentValue = new BigNumber(value);
      if (currentValue.isNaN()) {
        return t(keys.numberOnly);
      }

      if (integerOnly && !currentValue.isInteger()) {
        return t(keys.integerOnly);
      }

      if (currentValue.isLessThan(min)) {
        return t(keys.min, { value: min.toFormat(), unit });
      }

      if (!max && !balance) {
        return undefined;
      }

      const isGreater = max ? currentValue.isGreaterThan(max) : true;
      const isZeroBalance = balance?.isEqualTo(0);

      if (isGreater || isZeroBalance) {
        const balanceLessThanMax =
          max && (!balance ?? balance?.isLessThan(max));
        if (balanceLessThanMax) {
          return t(keys.max, { value: max, unit });
        }

        return t(keys.maxBalance);
      }
    },
    [
      balance,
      integerOnly,
      keys.integerOnly,
      keys.max,
      keys.maxBalance,
      keys.min,
      keys.numberOnly,
      keys.required,
      max,
      min,
      required,
      t,
      unit,
    ],
  );
};
